import React, { useEffect, useState } from "react";
import "./SearchResults.scss";
import DOMPurify from "dompurify";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  SEARCH_RESULTS_ATTRIBUTES,
  FILTER_FACETS,
  LABELKEYS_SEARCH_RESULTS,
  DEFAULT_SHOW_MORE_LENGTH,
  RECORDS_PER_PAGE_SEARCH_RESULTS,
  RECORDS_PER_PAGE_MANUAL_SEARCH_RESULTS,
  PATHNAMEURL,
} from "../../../helper/constants";
import FilterFacets from "./FilterFacets/FilterFacets";
import MetaData from "../../common/MetaData/MetaData";
import { Pagination } from "@mui/material";
import { useLocation } from "react-router-dom";
import { ScrollPageActions } from "../../../storage/reducers/scrollPageReducer";
import SearchResultHeader from "./SearchResultHeader/SearchResultHeader";
import { fetchLibraryData, searchWithManualModeUsingCache } from "./manualModeSearchUtils";

const SearchResults = (props) => {
  // Normal mode states
  const [searchData, setSearchData] = useState<any>([]);
  const [filterFacetData, setFilterFacetData] = useState<any>([]);
  const [pageSelected, setPageSelected] = useState(1);
  const [totalPage, setTotalPage] = useState();
  const [totalRecords, setTotalRecords] = useState(0);
  const [showPaginator, setShowPaginator] = useState<any>(false);

  // Manual mode states
  const [manualSubscriptionData, setManualSubscriptionData] = useState<any>([]);
  const [manualFilterFacetData, setManualFilterFacetData] = useState<any>([]);
  const [isLoadingManualData, setIsLoadingManualData] = useState(false);
  const [manualSearchData, setManualSearchData] = useState<any>([]);
  const [manualPageSelected, setManualPageSelected] = useState(1);
  const [manualTotalPage, setManualTotalPage] = useState(0);
  const [manualTotalRecords, setManualTotalRecords] = useState(0);
  const [manualShowPaginator, setManualShowPaginator] = useState(false);

  // Cache states
  const [cachedLibraryData, setCachedLibraryData] = useState<any[]>([]);
  const [libraryCacheValid, setLibraryCacheValid] = useState(false);
  const [cachedPubIdsString, setCachedPubIdsString] = useState<string>('');

  // NEW: Simple mode state - no Redux needed
  const [isManualMode, setIsManualMode] = useState(false);
  const [noPubIdsAvailable, setNoPubIdsAvailable] = useState(false);

  // Constants
  const manualRecordsPerPage = RECORDS_PER_PAGE_MANUAL_SEARCH_RESULTS;
  const recordsPerPage: any = RECORDS_PER_PAGE_SEARCH_RESULTS;

  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const facetObjStateVal = useSelector((state: any) => {
    return state.filteredFacets.facetObj;
  });

  const selectedFactesStateVal = useSelector((state: any) => {
    return state.filteredFacets.selectedFactes;
  });

  const attributes = props.attributes
    ? props.attributes
    : SEARCH_RESULTS_ATTRIBUTES;
  const showMoreVal = props.showMore ? props.showMore : false;
  const sortbyrelevance = [
    { label: "By Date", value: "by_date" },
    { label: "By Time", value: "by_time" },
    { label: "Accending", value: "accending" },
    { label: "Decending", value: "decending" },
    { label: "By Name", value: "by_name" },
  ];

  const { t } = useTranslation();

  const configData = useSelector(
    (state: any) => state.config?.configData || {}
  );

  const [data, setData] = useState("");

  // Helper functions
  const extractPubIds = (libraryData: any[]): string => {
    const pubIds = libraryData.map((item: any) =>
      item.pubId || item.publicationId || item.id
    );
    return pubIds.join(' ');
  };

  const fetchAllLibraryData = async () => {
    if (libraryCacheValid && cachedLibraryData.length > 0) {
      return cachedLibraryData;
    }

    let allLibraryData: any[] = [];
    const pageSize = 100;

    try {
      const firstResponse = await fetchLibraryData(1, pageSize);
      allLibraryData = [...firstResponse.data];
      const totalRecords = firstResponse.totalRecords;
      const totalPages = Math.ceil(totalRecords / pageSize);

      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page++) {
          const response = await fetchLibraryData(page, pageSize);
          allLibraryData = [...allLibraryData, ...response.data];
        }
      }

      setCachedLibraryData(allLibraryData);
      setLibraryCacheValid(true);

      // Check if we have any data with pubIds
      const pubIds = extractPubIds(allLibraryData);
      setNoPubIdsAvailable(!pubIds || pubIds.trim() === '');

      return allLibraryData;
    } catch (error) {
      // Set no pubIds available on error
      setNoPubIdsAvailable(true);
      throw error;
    }
  };

  const getFilteredSearchData = (data) => {
    setSearchData(data);
    setFilterFacetData(data?.aggregations);
  };

  //  Button click handler to switch to manual mode
  const handleSwitchToManualMode = async () => {
    if (isManualMode) return; // Already in manual mode

    setIsManualMode(true);
    setManualPageSelected(1);

    if (props?.searchval?.trim()) {
      await handleManualModeSearch();
    }
  };

  //  Button click handler to switch back to normal mode
  const handleSwitchToNormalMode = () => {
    if (!isManualMode) return; // Already in normal mode

    setIsManualMode(false);

    // Clear manual mode data
    setManualSubscriptionData([]);
    setManualFilterFacetData([]);
    setManualTotalRecords(0);
    setManualPageSelected(1);

    // Clear cache
    setCachedPubIdsString('');
    setLibraryCacheValid(false);
    setCachedLibraryData([]);

    // Reset to page 1 in normal mode
    setPageSelected(1);

    // If there's a parent page handler, call it to refresh data
    if (props.onPageSelected) {
      props.onPageSelected(1);
    }
  };

  const handleManualModeSearch = async () => {

    if (!props.searchval?.trim()) {
      return;
    }

    setManualSubscriptionData([]);
    setManualFilterFacetData([]);
    setIsLoadingManualData(true);
    setNoPubIdsAvailable(false); // Reset state

    try {
      let pubIdsString = cachedPubIdsString;
      if (!pubIdsString) {
        const libraryData = await fetchAllLibraryData();
        pubIdsString = extractPubIds(libraryData);
        setCachedPubIdsString(pubIdsString);
      }

      // Check if we have pubIds before searching
      if (!pubIdsString || pubIdsString.trim() === '') {
        setNoPubIdsAvailable(true);
        setManualSubscriptionData([]);
        setManualFilterFacetData([]);
        setManualTotalRecords(0);
        return;
      }

      const searchQuery = props.searchval === "*" ? "" : props.searchval;
      const manualSearchResults = await searchWithManualModeUsingCache(
        searchQuery,
        1,
        pubIdsString,
        manualRecordsPerPage
      );


      if (manualSearchResults?.message?.isSuccess) {
        const resultData = manualSearchResults.message.result;
        setManualSubscriptionData(resultData);
        setManualFilterFacetData(resultData?.aggregations);

        const manualTotalRecords = manualSearchResults?.message?.result?.hits?.total?.value || 0;
        setManualTotalRecords(manualTotalRecords);

        const numberOfPages = countTotalPagesManual(manualTotalRecords);
        showHidePaginatorManual(numberOfPages);
      } else {
        console.warn("Manual search failed:", manualSearchResults?.message?.result);
        setManualSubscriptionData([]);
        setManualFilterFacetData([]);
        setManualTotalRecords(0);
      }
    } catch (error) {
      console.error("Error in manual mode search:", error);
      setManualSubscriptionData([]);
      setManualFilterFacetData([]);
      setManualTotalRecords(0);
      setNoPubIdsAvailable(true); // Set flag on error
    } finally {
      setIsLoadingManualData(false);
    }
  };

  const countTotalPages = (records) => {
    const numberOfPages: any = Math.ceil(records / recordsPerPage);
    setTotalPage(numberOfPages);
    return numberOfPages;
  };

  const countTotalPagesManual = (records: number) => {
    const numberOfPages: any = Math.ceil(records / manualRecordsPerPage);
    setManualTotalPage(numberOfPages);
    return numberOfPages;
  };

  const searchBlankData = () => {
    if (
      searchData?.hits?.total !== undefined &&
      props?.searchval?.trim() == "" &&
      props.isStartOver === false
    ) {
      props?.setShowBrowseBy(true);
    }
  };

  const showHidePaginator = (pages) => {
    pages > 1 ? setShowPaginator(true) : setShowPaginator(false);
  };

  const showHidePaginatorManual = (pages: number) => {
    pages > 1 ? setManualShowPaginator(true) : setManualShowPaginator(false);
  };


  // Simplified useEffect - no toggle dependency
  useEffect(() => {

    searchBlankData();
    // FIX: Reset to normal mode when search value is empty
    if (isManualMode && (!props?.searchval?.trim() || props?.searchval?.trim() === "")) {
      setIsManualMode(false);

      // Clear manual mode data
      setManualSubscriptionData([]);
      setManualFilterFacetData([]);
      setManualTotalRecords(0);
      setManualPageSelected(1);

      // Clear cache
      setCachedPubIdsString('');
      setLibraryCacheValid(false);
      setCachedLibraryData([]);

      //claer page of normal
      setPageSelected(1);
    }
    // Only update normal mode data when not in manual mode
    if (!isManualMode) {

      setSearchData(props?.dataSearch);
      setFilterFacetData(props?.dataSearch?.aggregations);

      setPageSelected(props.currentPage);

      setTotalRecords(props?.dataSearch?.hits?.total?.value);

      const numberOfPages = countTotalPages(props?.dataSearch?.hits?.total?.value);
      const pagiantorFlag = props.paginator ? props.paginator : false;
      if (pagiantorFlag) {
        showHidePaginator(numberOfPages);
      }
    }
  }, [props.load, props?.dataSearch, props?.showBrowseBy, props.currentPage, isManualMode]);

  // Separate useEffect to handle search value changes
  useEffect(() => {
    // When search value changes and we're in manual mode, switch back to normal mode
    if (isManualMode && props.load === false && props?.dataSearch) {
      handleSwitchToNormalMode();
    }
  }, [props.searchval, props.load, props?.dataSearch]);
  // Helper functions to get current values based on mode
  const getCurrentDisplayData = () => {
    return isManualMode ? manualSubscriptionData : searchData;
  };

  const getCurrentTotalRecords = () => {
    return isManualMode ? manualTotalRecords : totalRecords;
  };

  const getCurrentPage = () => {
    return isManualMode ? manualPageSelected : pageSelected;
  };

  const getCurrentTotalPage = () => {
    return isManualMode ? manualTotalPage : totalPage;
  };

  const getCurrentShowPaginator = () => {
    return isManualMode ? manualShowPaginator : showPaginator;
  };

  const handlePageChange = (event, page) => {
    setPageSelected(page);
    dispatch(
      ScrollPageActions.getScrollPagePosition({
        pageNo: page,
      })
    );
    window.scrollTo(0, window.innerHeight / 2);


    if (pathname !== PATHNAMEURL && props.onPageSelected) {
      props.onPageSelected(page);
    }
  };

  const handlePageChangeManual = (event, page) => {
    window.scrollTo(0, window.innerHeight / 2);
    setManualPageSelected(page);
    if (props?.searchval?.trim()) {
      handleManualModeSearchWithPage(page);
    }
  };

  const handleManualModeSearchWithPage = async (pageNumber = 1) => {
    if (!props.searchval?.trim()) {
      return;
    }

    setIsLoadingManualData(true);
    try {
      const searchQuery = props.searchval === "*" ? "" : props.searchval;
      const manualSearchResults = await searchWithManualModeUsingCache(
        searchQuery,
        pageNumber,
        cachedPubIdsString,
        manualRecordsPerPage
      );

      if (manualSearchResults?.message?.isSuccess) {
        setManualSubscriptionData(manualSearchResults.message.result);
      }
    } catch (error) {
      console.error("Error in manual mode pagination:", error);
    } finally {
      setIsLoadingManualData(false);
    }
  };

  const getCurrentPageHandler = () => {
    return isManualMode ? handlePageChangeManual : handlePageChange;
  };

  const onClickStartOverBtn = () => {
    setTotalRecords(0);
    props.setIsStartOver(() => {
      return true;
    });
  };

  const resetToDefaultPage = () => {
    setPageSelected(1);
  };

  const onFilterFacetPaginate = (totalRecords) => {
    const numberOfPages: any = countTotalPages(totalRecords);
    numberOfPages > 1 ? setShowPaginator(true) : setShowPaginator(false);
    setPageSelected(1);
    setTotalRecords(totalRecords);
  };

  const handleMode = () => {
    props.isTitleLinkData();
  };

  return (
    <div
      className={`WrapperContainer ${props.className !== undefined ? props.className : ""
        }`}
    >

      {/* Loading indicator */}
      {isLoadingManualData && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading manual data
          <span className="loader__dot">.</span>
          <span className="loader__dot">.</span>
          <span className="loader__dot">.</span>
        </div>
      )}
      {!props?.load &&
        props?.searchval?.trim() != "" &&
        props?.facetFilter == true && (
          <SearchResultHeader
            apiPayload={selectedFactesStateVal}
            recordsCounter={getCurrentTotalRecords()}
            searchData={getCurrentDisplayData()}
            // Pass mode switching props
            isManualMode={isManualMode}
            isLoadingManualData={isLoadingManualData}
            onSwitchToManualMode={handleSwitchToManualMode}
            onSwitchToNormalMode={handleSwitchToNormalMode}
            showModeButtons={!!props?.searchval?.trim()}
            // Pass the no pubIds flag
            noPubIdsAvailable={noPubIdsAvailable}
          />
        )}

      {!props?.Watchlistflag && (
        <div className="header-serach-result">
          <span>{t(LABELKEYS_SEARCH_RESULTS.ITEMS)}</span>

          <Select
            aria-label="Search Sorting"
            className="sortby-relevance"
            options={sortbyrelevance}
            onChange={(opt) => console.log(opt?.label, opt?.value)}
            placeholder="Sort by Relevance"
          />
        </div>
      )}

      {/* Error messages */}
      {getCurrentDisplayData()?.hits?.total?.value === 0 && props?.load === false ? (
        props?.searchval?.trim() == "" && props?.isStartOver === false ? (
          <h1 className="erro-msg">
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  t(LABELKEYS_SEARCH_RESULTS.EMPTY_SEARCH)
                ),
              }}
            />
          </h1>
        ) : facetObjStateVal &&
          Object.keys(facetObjStateVal).includes("range") ? (
          <h1 className="FilterFacet erro-msg">
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  t(LABELKEYS_SEARCH_RESULTS.NO_DATE_DATE_RANGE)
                ),
              }}
            />
          </h1>
        ) : (
          facetObjStateVal !== "" &&
          props?.dataSearch?.hits?.total?.value === 0 &&
          props?.load === false &&
          props?.searchval !== " " && (
            <h1 className="erro-msg">
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    isManualMode
                      ? "No results found in your library for this search."
                      : t(LABELKEYS_SEARCH_RESULTS.NO_SEARCH_DATA_AVAILABLE)
                  ),
                }}
              />
            </h1>
          )
        )
      ) : getCurrentDisplayData()?.hits?.total !== undefined &&
        props.searchval?.trim() == "" &&
        props.isStartOver === false ? (
        <h1 className="erro-msg">
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                t(LABELKEYS_SEARCH_RESULTS.EMPTY_SEARCH)
              ),
            }}
          />
        </h1>
      ) : (
        ""
      )}

      <div className="main-panel-class">
        {getCurrentDisplayData()?.hits?.total?.value === undefined ||
          props?.Watchlistflag ? (
          ""
        ) : (
          <>
            {/* Only show filter facets in normal mode */}
            {!isManualMode && (
              <div className="Leftpanel">
                {props.facetFilter == true &&
                  props?.dataSearch?.hits?.total?.value > 0 &&
                  props.load === false &&
                  props?.searchval?.trim() != "" && (
                    <FilterFacets
                      filterFacetAttributes={FILTER_FACETS}
                      filterFacetValue={filterFacetData && filterFacetData}
                      searchVal={props.searchval}
                      getFilteredSearchData={getFilteredSearchData}
                      onClickStartOverBtn={onClickStartOverBtn}
                      currentPage={pageSelected}
                      onFilterFacetPaginate={onFilterFacetPaginate}
                      resetToDefaultPage={resetToDefaultPage}
                    />
                  )}
              </div>
            )}
          </>
        )}

        <div className={`search-class searchRecords ${isManualMode ? 'full-width' : ''}`}>
          {props.load === false && props?.searchval?.trim() != "" && (
            <MetaData
              attributes={attributes}
              searchData={getCurrentDisplayData()}
              showMore={showMoreVal}
              showMoreAttrLength={DEFAULT_SHOW_MORE_LENGTH}
              seacrchval={props.searchval}
              type={props.type}
              parentSubscriptionFlag={props.parentSubscriptionFlag}
              fileStreamerParams={
                props.fileStreamerParams ? props.fileStreamerParams : ""
              }
              udc={props?.udc}
              isTitleLink={props.isTitleLink}
            />
          )}
        </div>

        <div className="searchResultsPagination">
          {props.load === false &&
            props?.searchval?.trim() != "" &&
            getCurrentDisplayData()?.hits?.total?.value > 0 &&
            getCurrentShowPaginator() && (
              <Pagination
                className="myfavpagination"
                count={getCurrentTotalPage()}
                page={getCurrentPage()}
                onChange={getCurrentPageHandler()}
                hidePrevButton={false}
                hideNextButton={false}
                showFirstButton={true}
                showLastButton={true}
                boundaryCount={0}
                siblingCount={4}
                shape={"rounded"}
                color={"primary"}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;